/**
 * Подбор варианта техкарты для выделенной строки плана (макет v2-04).
 *
 * Главное требование founder: показывать И ₽/га, И сумму на площадь строки.
 * «48 200 ₽/га» само по себе ничего не решает — решение принимается по
 * «во что это встанет на 1 267 га» и по дельте к тому, что стоит сейчас.
 *
 * Предшественник — атрибут САМОЙ карты (ADR-066), поэтому вариант, заведённый
 * под другой предшественник, нельзя просто «повесить» на строку: он покрывает
 * свои строки. Для такого случая предлагаем честное действие — копию под этот
 * предшественник (это и есть «сделать новый вариант под предшественник» из
 * макета), а не молчаливое назначение, которое ничего бы не изменило.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  cardOptionsForRow,
  formatHa,
  formatMln,
  formatRubPerHa,
  formatSignedPerHa,
  rowTitle,
  type CardOption,
  type PlanRow,
} from '@/lib/planning/planVariant';
import type { TechCard } from '@/lib/planning/techCards';

export interface AssignAction {
  cardId: string;
  row: PlanRow;
  /** Карта заведена под другой предшественник — назначаем через копию. */
  needsFork: boolean;
  /** Карта соседнего хозяйства — берём её копию в своё хозяйство (AK-770). */
  adoptRequired: boolean;
}

export function AssignPanel({
  row,
  cards,
  cardsLoading,
  canWrite,
  busy,
  predecessorNames,
  onAssign,
  onUnassign,
  onOpenTechCards,
}: {
  row: PlanRow;
  cards: TechCard[];
  cardsLoading: boolean;
  canWrite: boolean;
  busy: boolean;
  predecessorNames: Record<string, string>;
  onAssign: (action: AssignAction) => void;
  onUnassign: () => void;
  onOpenTechCards: () => void;
}) {
  const options = cardOptionsForRow(cards, row, predecessorNames);
  const title = `${row.crop_name ?? 'Культура'} ${rowTitle(row)}`;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold text-foreground">Назначение варианта</h2>
          <span className="ml-auto text-xs text-muted-foreground">
            выбрана строка · {formatHa(row.area_ha)}
          </span>
        </div>

        <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
            <span>{title}</span>
            <span className="text-muted-foreground">· {formatHa(row.area_ha)}</span>
            {row.yield_c_ha !== null && (
              <span className="text-muted-foreground">
                · целевая урожайность {Math.round(row.yield_c_ha)} ц/га
              </span>
            )}
            <Button variant="outline" size="sm" className="ml-auto" onClick={onOpenTechCards}>
              Открыть техкарты культуры
            </Button>
          </div>

          {cardsLoading && <Skeleton className="mt-3 h-24 w-full" />}

          {!cardsLoading && options.length === 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              У этой культуры пока нет ни одного варианта техкарты на сезон. Заведите его в техкартах —
              тогда будет что назначать.
            </p>
          )}

          {!cardsLoading && options.length > 0 && (
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
              {options.map((option) => (
                <OptionTile
                  key={option.id}
                  option={option}
                  row={row}
                  canWrite={canWrite}
                  busy={busy}
                  onAssign={onAssign}
                />
              ))}
            </div>
          )}

          <p className="mt-3 text-xs leading-5 text-muted-foreground">
            Урожайность берётся из плана, а не из техкарты: одна техкарта может стоять на строках с
            разной целевой урожайностью.
          </p>

          {row.tech_card && canWrite && (
            <Button variant="outline" size="sm" className="mt-3" disabled={busy} onClick={onUnassign}>
              Снять назначение «{row.tech_card.name}»
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function OptionTile({
  option,
  row,
  canWrite,
  busy,
  onAssign,
}: {
  option: CardOption;
  row: PlanRow;
  canWrite: boolean;
  busy: boolean;
  onAssign: (action: AssignAction) => void;
}) {
  const mismatched = option.predecessorName !== null && !option.exactPredecessor;
  return (
    <div
      className={
        option.isCurrent
          ? 'rounded-lg border border-primary bg-primary/5 p-3'
          : 'rounded-lg border border-border p-3'
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-foreground">{option.name}</span>
        {option.exactPredecessor && <Badge variant="primary">подходит</Badge>}
        {option.isCurrent && <Badge variant="outline">назначен сейчас</Badge>}
        {option.scope === 'company' && <Badge variant="outline">карта компании</Badge>}
        {option.scope === 'other_farm' && (
          <Badge variant="outline">хозяйство {option.farmName ?? 'соседнее'}</Badge>
        )}
      </div>
      <div className="mt-1 text-xs leading-5 text-muted-foreground">
        {option.operationsCount ? `${option.operationsCount} операций · ` : ''}
        {option.predecessorName
          ? `рассчитан на предшественник ${option.predecessorName.toLowerCase()}`
          : 'общий вариант культуры (без предшественника)'}
      </div>
      {/* Карта заведена на соседнюю запись справочника («Подсолнечник» под
          строкой «Подсолнечник масличный»). Молчать нельзя: выбор должен
          оставаться осознанным, а не выглядеть ошибкой подбора. */}
      {option.familyCrop && option.cropName && (
        <div className="mt-1 text-xs leading-5 text-muted-foreground">
          заведён на культуру «{option.cropName}»
        </div>
      )}
      <div className="mt-2 text-base font-semibold tabular-nums text-foreground">
        {formatRubPerHa(option.costPerHa)}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        по {formatHa(row.area_ha)} → {formatMln(option.costTotal)} затрат
        {option.deltaPerHa !== null && (
          <span className={option.deltaPerHa < 0 ? ' text-primary' : ' text-destructive'}>
            {' · '}
            {formatSignedPerHa(option.deltaPerHa)}
          </span>
        )}
      </div>

      {canWrite && !option.isCurrent && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              onAssign({ cardId: option.id, row, needsFork: mismatched, adoptRequired: option.adoptRequired })
            }
          >
            {option.adoptRequired
              ? 'Взять копию в своё хозяйство'
              : mismatched
                ? 'Скопировать под этот предшественник'
                : option.exactPredecessor
                  ? 'Назначить этой строке'
                  : 'Назначить культуре'}
          </Button>
        </div>
      )}
      {canWrite && !option.isCurrent && option.adoptRequired && (
        <p className="mt-2 text-xs text-muted-foreground">
          Карта другого хозяйства: назначим её копию, ваши правки не заденут чужой план.
        </p>
      )}
      {canWrite && !option.isCurrent && !option.adoptRequired && !option.exactPredecessor && !mismatched && (
        <p className="mt-2 text-xs text-muted-foreground">
          Общий вариант встанет на все строки культуры, у которых нет своего варианта под предшественник.
        </p>
      )}
    </div>
  );
}
